"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ArithmeticOperation,
  ArithmeticView,
  type ComplexContext,
  PolarView,
  PowerRootView,
} from "@/components/tools/algebra/complex-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type ComplexError,
  parseComplex,
} from "@/lib/tool-kit/algebra/complex";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import type { NumberStyle } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const COMPLEX_NUMBER_CALCULATOR_STRINGS = {
  mode: "What do you want?",
  modes: {
    arithmetic: "Arithmetic: z₁ and z₂",
    polar: "Polar and exponential form",
    powers: "Powers and roots (De Moivre)",
  },
  first: "z₁",
  second: "z₂",
  numberHint:
    "Write it as 3 + 4i, −2i, 1/2 − 3/4 i or (1 + i)(2 − i). Use i (or j) for the imaginary unit.",
  operation: "Operation",
  operations: {
    add: "Add: z₁ + z₂",
    subtract: "Subtract: z₁ − z₂",
    multiply: "Multiply: z₁ × z₂",
    divide: "Divide: z₁ ÷ z₂",
  },
  exponent: "n (whole number, 2 to 24)",
  exponentHint: "Used for both zⁿ and the n-th roots of z₁.",
  results: "Result",
  result: "Result",
  resultPoint: "result",
  decimal: "As decimals",
  modulus: "Modulus |z|",
  argument: "Argument θ",
  argumentRadians: "Argument θ (radians)",
  argumentDegrees: "Argument θ (degrees)",
  undefinedArgument: "Undefined",
  conjugate: "Conjugate",
  reciprocal: "Reciprocal 1/z",
  opposite: "Opposite −z",
  workingHeading: "The working",
  stepFormula: "Use the rule.",
  stepSubstitute: "Substitute the numbers.",
  stepEvaluate: "Work out the products.",
  stepConjugate:
    "To divide, multiply the top and the bottom by the conjugate of the divisor. That makes the bottom a real number.",
  stepDenominator: "The bottom becomes c² + d², which is real.",
  stepNumerator: "Multiply out the top.",
  stepDivideParts: "Divide the real and imaginary parts by the bottom.",
  divideByZero: "Division by zero is not defined. Enter a non-zero z₂.",
  zeroPolar:
    "Zero has modulus 0 but no argument, so it has no polar form, no non-trivial roots and no angle. Enter a non-zero number.",
  stepModulus: "The modulus is the distance from the origin.",
  stepArgument:
    "The argument θ is the angle from the positive real axis, between −180° and 180°. This number {position}.",
  stepPolar: "Polar form.",
  stepExponential: "Exponential (Euler) form, with θ in radians.",
  argumentNote:
    "Arguments are given as the principal value, in the range −π < θ ≤ π. Add 2π (360°) for the equivalent angle from 0 to 2π.",
  positions: {
    origin: "is the origin",
    "positive-real": "lies on the positive real axis",
    "negative-real": "lies on the negative real axis",
    "positive-imaginary": "lies on the positive imaginary axis",
    "negative-imaginary": "lies on the negative imaginary axis",
    "quadrant-1": "is in the first quadrant, so θ = arctan(b/a)",
    "quadrant-2": "is in the second quadrant, so θ = arctan(b/a) + 180°",
    "quadrant-3": "is in the third quadrant, so θ = arctan(b/a) − 180°",
    "quadrant-4": "is in the fourth quadrant, so θ = arctan(b/a)",
  },
  powerLabel: "z^{n}",
  powerDecimal: "As decimals",
  rootRadius: "Modulus of each root",
  powerHeading: "z^{n} exactly, and by De Moivre's theorem",
  stepExact: "Multiply z by itself exactly, by repeated squaring.",
  stepDeMoivre:
    "De Moivre's theorem raises the modulus to the power and multiplies the angle by it (decimals are rounded to four places):",
  rootsHeading: "Roots of order {n}",
  rootsIntro:
    "The n-th roots of z share the modulus r^(1/n) and are spaced 360°/n apart: the vertices of a regular polygon. Angles are (θ + 360°k)/n for k = 0, 1, …, n − 1.",
  rootIndex: "k",
  rootAngle: "Angle",
  rootValue: "Root",
  planeHeading: "Complex plane",
  planeTitle:
    "The complex plane with each number drawn as an arrow from the origin",
  rootsPlaneTitle:
    "The complex plane showing the {n} roots on a circle, joined as a regular polygon",
  downloadPng: "Download diagram (PNG)",
  enter: "Enter a complex number to begin.",
  needSecond: "Enter z₂ as well.",
  badExponent: "Enter n as a whole number from 2 to 24.",
  errors: {
    empty: "Enter a complex number.",
    syntax: "That is not a complex number. Check near “{token}”.",
    letter:
      "The letter “{token}” is not allowed. Use i (or j) for the imaginary unit and no other letters.",
    power: "Powers must be whole numbers.",
    "divide-by-variable":
      "Dividing by i is not read here. Use the Divide operation with z₂ instead.",
  },
} as const;

type Strings = typeof COMPLEX_NUMBER_CALCULATOR_STRINGS;
type Mode = keyof Strings["modes"];

const DECIMAL_INPUT = /[.,]/;
const OPERATION_KEYS: ArithmeticOperation[] = [
  "add",
  "subtract",
  "multiply",
  "divide",
];

function failure(error: ComplexError, token: string, strings: Strings): string {
  return t(strings.errors[error], { token });
}

export default function ComplexNumberCalculatorWidget() {
  const strings = useToolUi(COMPLEX_NUMBER_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("arithmetic");
  const [first, setFirst] = useState("3 + 4i");
  const [second, setSecond] = useState("1 − 2i");
  const [op, setOp] = useState<ArithmeticOperation>("divide");
  const [exponent, setExponent] = useState("3");
  const style: NumberStyle = DECIMAL_INPUT.test(`${first} ${second}`)
    ? "decimal"
    : "fraction";
  const c: ComplexContext = { strings, style, mark: decimalMarkOf(fmt) };
  const a = parseComplex(first);
  const b = parseComplex(second);
  const n = parseIntegerInput(exponent);
  let body = <Notice>{strings.enter}</Notice>;
  if (first.trim() !== "") {
    if (!a.ok) {
      body = <ErrorNotice>{failure(a.error, a.token, strings)}</ErrorNotice>;
    } else if (mode === "polar") {
      body = <PolarView c={c} z={a.value} />;
    } else if (mode === "powers") {
      body =
        n === null || n < 2 || n > 24 ? (
          <ErrorNotice>{strings.badExponent}</ErrorNotice>
        ) : (
          <PowerRootView c={c} n={n} z={a.value} />
        );
    } else if (second.trim() === "") {
      body = <Notice>{strings.needSecond}</Notice>;
    } else if (b.ok) {
      body = <ArithmeticView a={a.value} b={b.value} c={c} op={op} />;
    } else {
      body = <ErrorNotice>{failure(b.error, b.token, strings)}</ErrorNotice>;
    }
  }
  return (
    <>
      <PillGroup
        label={strings.mode}
        name="cnc-mode"
        onChange={setMode}
        options={(["arithmetic", "polar", "powers"] as const).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.numberHint}
          id="cnc-first"
          label={strings.first}
          mono
          onChange={setFirst}
          value={first}
        />
        {mode === "arithmetic" ? (
          <TextField
            id="cnc-second"
            label={strings.second}
            mono
            onChange={setSecond}
            value={second}
          />
        ) : null}
        {mode === "powers" ? (
          <TextField
            hint={strings.exponentHint}
            id="cnc-exponent"
            label={strings.exponent}
            mono
            numeric
            onChange={setExponent}
            value={exponent}
          />
        ) : null}
      </div>
      {mode === "arithmetic" ? (
        <SelectField
          id="cnc-op"
          label={strings.operation}
          onChange={setOp}
          options={OPERATION_KEYS.map((value) => ({
            value,
            label: strings.operations[value],
          }))}
          value={op}
        />
      ) : null}
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
